<template>
    <div class="flex flex-col gap-4">
        <!-- Modal de criação -->
        <Modal
            :open="showModal"
            title="Gerar token de API"
            @close="closeCreateModal"
        >
            <div v-if="!createdToken" class="flex flex-col space-y-3">
                <label class="text-sm text-gray-600 dark:text-gray-300">
                    Nome do token
                </label>
                <input
                    class="text-input"
                    v-model="name"
                    placeholder="Ex: MCP monitoring - meu notebook"
                    @keyup.enter="createToken"
                />
                <SubmitButton
                    :disabled="loading || !name.trim()"
                    :loading="loading"
                    @click="createToken"
                    class="w-full"
                >
                    Gerar token
                </SubmitButton>
            </div>

            <!-- Exibição única do token recém-criado -->
            <div v-else class="flex flex-col space-y-3">
                <div class="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-500/40 dark:bg-amber-500/10">
                    <ExclamationTriangleIcon class="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                    <p class="text-sm text-amber-800 dark:text-amber-200">
                        Copie o token agora. Por segurança, ele
                        <strong>não será exibido novamente</strong>.
                    </p>
                </div>

                <div class="flex items-center gap-2">
                    <input
                        ref="tokenField"
                        class="text-input font-mono text-xs"
                        :value="createdToken"
                        readonly
                        @focus="$event.target.select()"
                    />
                    <button
                        type="button"
                        @click="copyToken"
                        class="flex shrink-0 items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600"
                    >
                        <component :is="copied ? CheckIcon : ClipboardDocumentIcon" class="h-4 w-4" />
                        {{ copied ? "Copiado" : "Copiar" }}
                    </button>
                </div>

                <button
                    type="button"
                    @click="closeCreateModal"
                    class="w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600"
                >
                    Concluir
                </button>
            </div>
        </Modal>

        <!-- Modal de revogação -->
        <Modal
            :open="showDeleteModal"
            title="Revogar token"
            @close="showDeleteModal = false"
        >
            <div class="flex flex-col space-y-4 dark:text-white">
                <p class="text-gray-900 dark:text-white">
                    Tem certeza que deseja revogar este token? Integrações que o
                    utilizam vão parar de funcionar imediatamente.
                </p>
                <div class="flex flex-col space-y-2">
                    <button
                        @click="showDeleteModal = false"
                        class="w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                        Cancelar
                    </button>
                    <button
                        @click="confirmDelete"
                        class="w-full rounded-md border border-transparent bg-red-600 px-4 py-2 text-xs font-medium text-white hover:bg-red-700"
                    >
                        Revogar
                    </button>
                </div>
            </div>
        </Modal>

        <div class="flex flex-row items-center gap-2">
            <div class="flex items-center gap-2">
                <KeyIcon class="h-6 w-6 text-gray-500" />
                <h3 class="text-lg font-bold dark:text-white">Tokens de API</h3>
            </div>
            <SubmitButton
                class="ml-auto"
                :loading="loading"
                @click="openCreateModal"
            >
                <PlusIcon class="mr-1 h-5 w-5" />
                Gerar token
            </SubmitButton>
            <ArrowPathIcon
                class="ml-1 h-6 w-6 cursor-pointer text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                :class="{ 'animate-spin': loading }"
                @click="fetch"
            />
        </div>

        <div class="flex flex-col gap-4">
            <EmptyState
                v-if="tokens.length === 0"
                class="mb-4"
                title="Nenhum token encontrado"
                description="Gere um token para usar em integrações e no MCP de monitoramento"
            />
            <div
                v-for="token in tokens"
                :key="token.id"
                class="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4 transition-all duration-200 hover:shadow-md dark:border-slate-700 dark:bg-slate-800/25"
            >
                <div class="flex flex-wrap items-center gap-4">
                    <div class="flex items-center gap-3">
                        <KeyIcon class="h-5 w-5 text-gray-500" />
                        <span class="font-medium text-gray-700 dark:text-white">{{ token.name }}</span>
                    </div>
                    <div class="flex items-center gap-2">
                        <ClockIcon class="h-4 w-4 shrink-0 text-gray-400" />
                        <span class="text-sm text-gray-500 dark:text-gray-400">
                            Criado em {{ formatDate(token.created_at) }}
                        </span>
                    </div>
                    <span class="text-sm text-gray-500 dark:text-gray-400">
                        Último uso: {{ token.last_used_at ? formatDate(token.last_used_at) : "nunca" }}
                    </span>
                </div>
                <button
                    @click="askDelete(token.id)"
                    class="shrink-0 rounded-full p-2 text-gray-400 transition-colors duration-200 hover:bg-red-50 hover:text-red-500"
                >
                    <TrashIcon class="h-5 w-5" />
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import SubmitButton from "../../forms/SubmitButton.vue";
import EmptyState from "../../layouts/EmptyState.vue";
import Modal from "../../layouts/Modal.vue";
import dayjs from "dayjs";
import {
    ArrowPathIcon,
    TrashIcon,
    KeyIcon,
    PlusIcon,
    ClockIcon,
    ClipboardDocumentIcon,
    CheckIcon,
    ExclamationTriangleIcon,
} from "@heroicons/vue/24/outline";

export default {
    components: {
        SubmitButton,
        EmptyState,
        Modal,
        ArrowPathIcon,
        TrashIcon,
        KeyIcon,
        PlusIcon,
        ClockIcon,
        ClipboardDocumentIcon,
        CheckIcon,
        ExclamationTriangleIcon,
    },

    data() {
        return {
            tokens: [],
            loading: false,
            showModal: false,
            showDeleteModal: false,
            name: "",
            createdToken: null,
            copied: false,
            tokenToDelete: null,
            // Referências aos ícones para uso dinâmico no template (:is).
            CheckIcon,
            ClipboardDocumentIcon,
        };
    },

    mounted() {
        this.fetch();
    },

    methods: {
        formatDate(date) {
            return dayjs(date).format("DD/MM/YYYY HH:mm");
        },

        openCreateModal() {
            this.name = "";
            this.createdToken = null;
            this.copied = false;
            this.showModal = true;
        },

        closeCreateModal() {
            this.showModal = false;
            this.createdToken = null;
            this.copied = false;
            this.name = "";
        },

        createToken() {
            if (!this.name.trim() || this.loading) {
                return;
            }

            this.loading = true;

            axios
                .post("/api/user/tokens", { name: this.name.trim() })
                .then(({ data }) => {
                    this.createdToken = data.token.plain_text_token;
                    this.fetch();
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        async copyToken() {
            try {
                await navigator.clipboard.writeText(this.createdToken);
            } catch (e) {
                // Fallback quando a Clipboard API não está disponível.
                const field = this.$refs.tokenField;
                if (field) {
                    field.select();
                    document.execCommand("copy");
                }
            }
            this.copied = true;
            setTimeout(() => {
                this.copied = false;
            }, 2000);
        },

        askDelete(id) {
            this.tokenToDelete = id;
            this.showDeleteModal = true;
        },

        confirmDelete() {
            if (!this.tokenToDelete) {
                return;
            }

            axios
                .delete(`/api/user/tokens/${this.tokenToDelete}`)
                .then(() => {
                    this.showDeleteModal = false;
                    this.tokenToDelete = null;
                    this.fetch();
                });
        },

        fetch() {
            this.loading = true;

            axios
                .get("/api/user/tokens")
                .then(({ data }) => {
                    this.tokens = data.tokens;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
    },
};
</script>
